import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, Pause, Grab, Minus, Plus as PlusIcon, Search, Loader2 } from 'lucide-react';

const modalStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.95)', zIndex: 999999,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
};

const zoomBarStyle = {
  background: 'rgba(45, 45, 45, 0.8)', backdropFilter: 'blur(10px)',
  padding: '8px 16px', borderRadius: '8px', display: 'flex', flexDirection: 'column',
  alignItems: 'center', gap: '6px', border: '1px solid #3e3e42',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 10001
};

const bottomContainerStyle = {
  position: 'absolute', bottom: '30px', left: '50%', transform: 'translateX(-50%)',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', zIndex: 10001
};

const formatTime = (s) => {
  if (!s && s !== 0) return '00:00';
  const min = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${min}:${sec < 10 ? '0' : ''}${sec}`;
};

const formatGenerationTime = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  if (seconds < 60) return `${seconds}秒`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}分${secs}秒` : `${mins}分`;
};

export default function FullscreenModal({ url, fallbackUrl, type, onClose, generationTime }) {
  const videoRef = useRef(null);
  const imageContainerRef = useRef(null);
  const imgRef = useRef(null);
  const triedFallbackRef = useRef(false);
  // 判断 URL 是否可被浏览器加载（排除 local-cache://、裸 UUID 等无效格式）
  const isLoadableUrl = (u) => u && (u.startsWith('http') || u.startsWith('blob:') || u.startsWith('data:') || u.startsWith('/'));
  const effectiveUrl = isLoadableUrl(url) ? url : (isLoadableUrl(fallbackUrl) ? fallbackUrl : url);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [mediaLoaded, setMediaLoaded] = useState(false);
  
  // 图像缩放和拖动状态
  const [zoom, setZoom] = useState(0.8);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  // 视频加载时自动播放
  useEffect(() => {
    if (type === 'video' && videoRef.current) {
      videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [type]);
  
  // 图像加载后适应屏幕
  useEffect(() => {
    if (type !== 'video' && imgRef.current) {
      const handleImageLoad = () => {
        const img = imgRef.current;
        if (!img) return;
        setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        const fitZoom = Math.min(
          (window.innerWidth * 0.95) / img.naturalWidth,
          (window.innerHeight * 0.95 - 100) / img.naturalHeight
        );
        setZoom(fitZoom || 0.8);
        setOffset({ x: 0, y: 0 });
      };
      
      if (imgRef.current.complete) {
        handleImageLoad();
      } else {
        imgRef.current.addEventListener('load', handleImageLoad);
        return () => imgRef.current?.removeEventListener('load', handleImageLoad);
      }
    }
  }, [type]);
  
  // 快捷键处理
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
      // 空格键：视频播放/暂停
      if (e.key === ' ' && type === 'video') {
        e.preventDefault();
        if (videoRef.current) {
          if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
          else { videoRef.current.pause(); setPlaying(false); }
        }
        return;
      }
      if (type !== 'video') {
        if (e.key === ' ') {
          e.preventDefault();
          setIsSpacePressed(true);
        }
        if (e.ctrlKey || e.metaKey) {
          if (e.key === '=' || e.key === '+') {
            e.preventDefault();
            setZoom(prev => Math.min(prev + 0.1, 5));
          } else if (e.key === '-' || e.key === '_') {
            e.preventDefault();
            setZoom(prev => Math.max(prev - 0.1, 0.1));
          } else if (e.key === '0') {
            e.preventDefault();
            handleFitScreen();
          } else if (e.key === '1') {
            e.preventDefault();
            handleActualPixels();
          }
        } else {
          if (e.key === 'z' || e.key === 'Z') {
            handleFitScreen();
          }
          if (e.key === '1') {
            handleActualPixels();
          }
        }
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === ' ') {
        setIsSpacePressed(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [onClose, type]);
  
  const togglePlay = (e) => {
    e.stopPropagation();
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
    setPlaying(!playing);
  };
  
  // 适应屏幕 - 完整显示，不裁切
  const handleFitScreen = () => {
    if (!imgRef.current) return;
    const img = imgRef.current;
    const fitZoom = Math.min(
      (window.innerWidth * 0.95) / img.naturalWidth,
      (window.innerHeight * 0.95 - 100) / img.naturalHeight
    );
    setZoom(fitZoom || 0.8);
    setOffset({ x: 0, y: 0 });
  };
  
  // 实际像素 (100%)
  const handleActualPixels = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };
  
  // 拖动画布
  const handleMouseDown = (e) => {
    if (type !== 'video' && (isSpacePressed || e.button === 1)) {
      e.preventDefault();
      setIsDragging(true);
      dragStartPos.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    }
  };
  
  const handleMouseMove = (e) => {
    if (isDragging) {
      setOffset({
        x: e.clientX - dragStartPos.current.x,
        y: e.clientY - dragStartPos.current.y
      });
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // 📱 iPad 触摸手势：双指捏合 = 缩放（替代 PC 的 Ctrl +/-），单指拖动 = 平移图片。视频不参与。
  //   原生监听 + preventDefault：既做我们的缩放/平移，又挡掉浏览器对整页/图片的默认双指缩放和滚动。
  useEffect(() => {
    if (type === 'video') return;
    const el = imageContainerRef.current;
    if (!el) return;
    let startDist = 0, startZoom = 1;
    // 单指平移：记录起始触点与起始偏移
    let panStart = null, panStartOffset = { x: 0, y: 0 };
    const dist = (ts) => Math.hypot(ts[0].clientX - ts[1].clientX, ts[0].clientY - ts[1].clientY);
    const onStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        panStart = null;  // 进入双指缩放，取消单指平移
        startDist = dist(e.touches);
        setZoom(z => { startZoom = z; return z; });
      } else if (e.touches.length === 1) {
        // 单指按下：准备平移。用函数式读取当前 offset 作为拖动基准。
        panStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        setOffset(o => { panStartOffset = o; return o; });
      }
    };
    const onMove = (e) => {
      if (e.touches.length === 2 && startDist > 0) {
        e.preventDefault();
        const ratio = dist(e.touches) / startDist;
        setZoom(Math.max(0.1, Math.min(5, startZoom * ratio)));
      } else if (e.touches.length === 1 && panStart) {
        e.preventDefault();
        setOffset({
          x: panStartOffset.x + (e.touches[0].clientX - panStart.x),
          y: panStartOffset.y + (e.touches[0].clientY - panStart.y)
        });
      }
    };
    const onEnd = (e) => {
      if (e.touches.length < 2) startDist = 0;
      if (e.touches.length === 0) panStart = null;
    };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [type]);

  return createPortal(
    <div style={modalStyle} onClick={onClose}>
      {/* ESC 提示 - 使用绘图版样式 */}
      <div style={{ 
        position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
        padding: '8px 16px', background: 'rgba(45, 45, 45, 0.8)', borderRadius: '8px',
        color: '#cccccc', fontSize: '13px', fontWeight: '500',
        border: '1px solid #3e3e42', backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
      }}>
        按 ESC 键退出全屏
      </div>
      
      {/* 关闭按钮 — zIndex 高于内容区，且自带 onClick：放大后图片容器铺满全屏也不挡关闭 */}
      <div
        onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onClose && onClose(); }}
        style={{ position: 'absolute', top: '20px', right: '20px', cursor: 'pointer', padding: '10px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%', zIndex: 10002 }}
      >
        <X size={24} color="#fff" />
      </div>

      {/* 内容区域 */}
      <div 
        ref={imageContainerRef}
        style={{
          flex: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          touchAction: 'none',  // 📱 禁浏览器默认双指缩放/平移，改由自定义捏合缩放（视频则纯禁用）
          cursor: type === 'video' ? 'default' : (isDragging ? 'grabbing' : (isSpacePressed ? 'grab' : 'default'))
        }}
        onClick={e => e.stopPropagation()}
        onMouseDown={handleMouseDown}
      >
        {type === 'video' ? (
          <video 
            ref={videoRef} src={effectiveUrl} loop
            // 🔗 crossOrigin="anonymous"：与画布 <video> 保持相同的 CORS 策略。
            //    若缺失，浏览器视为不同「安全源」请求，无法复用画布已缓冲的视频数据，
            //    导致全屏时重新从 R2/OSS 下载整个视频文件（表现为转圈等待）。
            crossOrigin="anonymous"
            style={{ maxHeight: '90vh', maxWidth: '90vw', boxShadow: '0 0 50px rgba(0,0,0,0.5)', opacity: mediaLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
            onTimeUpdate={() => setCurrentTime(videoRef.current.currentTime)}
            onLoadedMetadata={(e) => {
              setDuration(e.target.duration);
              setDimensions({ width: e.target.videoWidth, height: e.target.videoHeight });
            }}
            onCanPlay={() => setMediaLoaded(true)}
            onError={(e) => {
              if (!triedFallbackRef.current && fallbackUrl && e.target.src !== fallbackUrl) {
                triedFallbackRef.current = true;
                e.target.src = fallbackUrl;
              }
            }}
            onClick={togglePlay}
          />
        ) : (
          <img 
            ref={imgRef}
            src={effectiveUrl} 
            alt="Fullscreen" 
            style={{ 
              transform: `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              userSelect: 'none',
              maxWidth: 'none',
              maxHeight: 'none',
              opacity: mediaLoaded ? 1 : 0,
              backgroundImage: 'linear-gradient(45deg, #c8c8c8 25%, transparent 25%), linear-gradient(-45deg, #c8c8c8 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #c8c8c8 75%), linear-gradient(-45deg, transparent 75%, #c8c8c8 75%)',
              backgroundSize: '16px 16px',
              backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
              backgroundColor: '#e8e8e8',
            }} 
            draggable={false}
            onLoad={() => setMediaLoaded(true)}
            onError={(e) => {
              if (!triedFallbackRef.current && fallbackUrl && e.target.src !== fallbackUrl) {
                triedFallbackRef.current = true;
                e.target.src = fallbackUrl;
              }
            }}
          />
        )}
        {/* 加载中动画 — 居中旋转指示器 */}
        {!mediaLoaded && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12
          }}>
            <Loader2 size={28} color="#555" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ color: '#555', fontSize: 13, letterSpacing: '0.5px' }}>加载中</span>
          </div>
        )}
      </div>

      {/* 底部控制区 */}
      <div style={bottomContainerStyle} onClick={e => e.stopPropagation()}>
        {/* 分辨率显示 - 同图板节点样式 */}
        {dimensions.width > 0 && (() => {
          const pixels = dimensions.width * dimensions.height;
          let level = { text: 'HI', color: '#ef4444' };
          if (pixels < 1280 * 720) level = { text: 'LD', color: '#9ca3af' };
          else if (pixels < 1920 * 1080) level = { text: 'SD', color: '#22c55e' };
          else if (pixels < 3840 * 2160) level = { text: 'HD', color: '#eab308' };
          else if (pixels < 5120 * 2880) level = { text: '4K', color: '#f97316' };
          
          const timeText = formatGenerationTime(generationTime);

          return (
            <div style={{
              background: 'rgba(0,0,0,0.6)', 
              color: 'rgba(255,255,255,0.8)',
              fontSize: '14px', 
              padding: '6px 14px', 
              borderRadius: '8px',
              backdropFilter: 'blur(10px)', 
              zIndex: 5,
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              fontWeight: '600',
              border: '1px solid rgba(255,255,255,0.1)',
              marginBottom: '6px'
            }}>
              <span style={{ 
                  color: level.color, 
                  border: `2px solid ${level.color}`,
                  borderRadius: '4px',
                  padding: '1px 6px',
                  fontSize: '12px',
                  fontWeight: '900',
                  lineHeight: '1.2'
              }}>
                {level.text}
              </span>
              <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>{dimensions.width} × {dimensions.height}</span>
              {timeText && (
                <span style={{
                  color: 'rgba(255,255,255,0.5)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  padding: '1px 6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  lineHeight: '1.2'
                }}>
                  {timeText}
                </span>
              )}
            </div>
          );
        })()}

        {/* 图像缩放控制条 - 使用绘图版样式 */}
        {type !== 'video' && (
          <div style={zoomBarStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div 
                onClick={() => setIsSpacePressed(!isSpacePressed)}
                title="抓手工具 (空格)" 
                style={{ 
                  cursor: 'pointer', 
                  color: isSpacePressed ? '#4cc3ff' : '#fff', 
                  opacity: 0.8,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Grab size={18}/>
              </div>
              <div style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
              <div 
                onClick={() => setZoom(prev => Math.max(prev - 0.1, 0.1))} 
                title="缩小"
                style={{ 
                  cursor: 'pointer', 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  background: 'rgba(255,255,255,0.1)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}
              >
                <Minus size={14} color="#fff"/>
              </div>
              <div style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold', minWidth: '45px', textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </div>
              <div 
                onClick={() => setZoom(prev => Math.min(prev + 0.1, 5))} 
                title="放大"
                style={{ 
                  cursor: 'pointer', 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  background: 'rgba(255,255,255,0.1)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}
              >
                <PlusIcon size={14} color="#fff"/>
              </div>
              <div style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
              <div 
                onClick={handleActualPixels} 
                title={`实际像素 (${cmdKey} + 1)`} 
                style={{ 
                  cursor: 'pointer', 
                  color: zoom === 1 ? '#4cc3ff' : '#fff', 
                  display: 'flex',
                  alignItems: 'center',
                  background: zoom === 1 ? 'rgba(76, 195, 255, 0.1)' : 'transparent',
                  padding: '4px',
                  borderRadius: '4px'
                }}
              >
                <Search size={16}/>
              </div>
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.5px' }}>
              {cmdKey} +/- 缩放 | Z 适应窗口 | {cmdKey} + 1 实际像素
            </div>
          </div>
        )}

        {/* 视频控制条 */}
        {type === 'video' && (
          <div style={{ 
            width: '600px', 
            maxWidth: '90vw', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '15px', 
            padding: '15px 25px', 
            background: 'rgba(40,40,40,0.8)', 
            borderRadius: '30px', 
            backdropFilter: 'blur(10px)', 
            border: '1px solid rgba(255,255,255,0.1)' 
          }} onClick={e => e.stopPropagation()}>
            <div onClick={togglePlay} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              {playing ? <Pause size={24} fill="#fff" color="#fff" /> : <Play size={24} fill="#fff" color="#fff" />}
            </div>
            <input 
              type="range" min="0" max={duration || 0} step="0.1" 
              value={currentTime} 
              onChange={(e) => { videoRef.current.currentTime = parseFloat(e.target.value); setCurrentTime(parseFloat(e.target.value)); }}
              style={{ flex: 1, height: '4px', cursor: 'pointer', accentColor: '#3b82f6' }} 
            />
            <div style={{ color: '#fff', fontFamily: 'monospace', fontSize: '14px', minWidth: '90px', textAlign: 'right' }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}